import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useFilter } from '@spa/features/search_job/pc/contexts/FilterContext';
import { numberFormat } from '@src/common/service/numberFormatHelper';
import {
    getInitialValues,
    getRangeFromStatistics,
    isSliderDisabled,
    SalaryConstants,
} from '@src/common/service/salary/salaryHelper';

/**
 * 給与フィルターの状態管理を担当するカスタムフック
 * 
 * @param {Object} params - パラメータ
 * @param {string} params.salaryTypeParamKey - 給与種別パラメータキー
 * @param {string} params.salaryMinParamKey - 給与最小値パラメータキー
 * @param {string} params.salaryMaxParamKey - 給与最大値パラメータキー
 * @param {Object} params.salaryStatistics - 給与統計データ
 * @param {string} params.channelName - チャネル名
 * @param {Object} params.location - ロケーション情報
 * @param {Object} params.initialSalaryState - 初期給与状態
 * @returns {Object} 状態管理の結果
 */
const useSalaryFilterStateManager = ({
    salaryTypeParamKey,
    salaryMinParamKey,
    salaryMaxParamKey,
    salaryStatistics,
    channelName,
    location,
    initialSalaryState,
}) => {
    // === 状態管理 ===
    const { getFilterValue } = useFilter();

    // ローカル状態（モーダル内の一時編集値）
    const [localState, setLocalState] = useState({
        salaryType: initialSalaryState.salaryType,
        salaryMin: initialSalaryState.salaryMin,
        salaryMax: initialSalaryState.salaryMax
    });

    // 給与種別ごとの値を保持する状態
    const [salaryTypeValues, setSalaryTypeValues] = useState({
        [SalaryConstants.ALL_TYPE_ID]: { salaryMin: '', salaryMax: '' }
    });

    // モーダルの開閉状態
    const [isOpen, setIsOpen] = useState(false);

    // フォーマット表示用の状態
    const [formattedValues, setFormattedValues] = useState({
        min: '0',
        max: `${SalaryConstants.DEFAULT_MAX.toLocaleString()}`
    });

    // 遷移中フラグ（クリアボタン押下時にページ遷移完了までis-activeを維持）
    const [isNavigating, setIsNavigating] = useState(false);

    // モーダルが一度開かれたかどうかを追跡するフラグ
    const [hasModalBeenOpened, setHasModalBeenOpened] = useState(false);

    // === 参照設定 ===
    // スライダー・入力欄の参照
    const sliderRef = useRef(null);
    const sliderInstanceRef = useRef(null);
    const minInputRef = useRef(null);
    const maxInputRef = useRef(null);

    // === 計算値 ===
    // 選択中の給与種別ごとに統計情報がなければスライダー・入力欄を無効化
    const isSliderDisabledValue = isSliderDisabled(localState.salaryType, salaryStatistics);

    // 統計データから範囲を取得
    const { rangeMin, rangeMax } = useMemo(() => {
        return getRangeFromStatistics(
            salaryStatistics,
            localState.salaryType,
            SalaryConstants.DEFAULT_MIN,
            SalaryConstants.DEFAULT_MAX
        );
    }, [salaryStatistics, localState.salaryType]);

    // 初期値の計算
    const { initialMin, initialMax } = useMemo(() => {
        return getInitialValues(
            {
                typeId: localState.salaryType,
                min: localState.salaryMin,
                max: localState.salaryMax
            },
            localState.salaryType,
            rangeMin,
            rangeMax
        );
    }, [localState.salaryType, localState.salaryMin, localState.salaryMax, rangeMin, rangeMax]);

    /**
     * 給与種別を変更する際の処理
     * @param {string} newSalaryType - 新しい給与種別
     */
    const handleSalaryTypeChange = useCallback((newSalaryType) => {
        // 現在の種別の値を保存し、新しい種別の値を復元する処理を1つの関数で実行
        setSalaryTypeValues(prev => {
            // 現在の種別の値を保存
            const updatedSalaryTypeValues = {
                ...prev,
                [localState.salaryType]: {
                    salaryMin: localState.salaryMin,
                    salaryMax: localState.salaryMax
                }
            };

            // 新しい種別の値を復元（存在しない場合は空文字）
            const savedValues = updatedSalaryTypeValues[newSalaryType] || { salaryMin: '', salaryMax: '' };
            
            // ローカル状態も同時に更新
            setLocalState(prevLocalState => ({
                ...prevLocalState,
                salaryType: newSalaryType,
                salaryMin: savedValues.salaryMin,
                salaryMax: savedValues.salaryMax
            }));

            return updatedSalaryTypeValues;
        });
    }, [localState.salaryType, localState.salaryMin, localState.salaryMax]);

    /**
     * スライダー更新時のハンドラー
     * @param {Array} values - スライダーの値 [min, max]
     * @param {number} handle - ハンドルのインデックス
     */
    const handleSliderUpdate = useCallback((values, handle) => {
        const [minValue, maxValue] = values;
        setLocalState(prev => ({
            ...prev,
            salaryMin: minValue.toString(),
            salaryMax: maxValue.toString()
        }));
        setFormattedValues(prev => ({
            ...prev,
            min: numberFormat(+minValue, 0),
            max: numberFormat(+maxValue, 0)
        }));
    }, []);

    /**
     * スライダーの初期化処理
     * @param {number} initialMin - 初期最小値
     * @param {number} initialMax - 初期最大値
     * @param {number} rangeMin - 範囲最小値
     * @param {number} rangeMax - 範囲最大値
     * @param {boolean} isSliderDisabledValue - スライダー無効化フラグ
     */
    const initializeSlider = useCallback(async (initialMin, initialMax, rangeMin, rangeMax, isSliderDisabledValue) => {
        if (!sliderRef.current) return;
        if (sliderInstanceRef.current) {
            sliderInstanceRef.current.destroy();
            sliderInstanceRef.current = null;
        }
        const { default: noUiSlider } = await import('nouislider');
        noUiSlider.create(sliderRef.current, {
            start: [initialMin, initialMax],
            margin: 0,
            connect: true,
            handleAttributes: [
                { 'aria-label': 'lower' },
                { 'aria-label': 'upper' },
            ],
            range: { 'min': rangeMin, 'max': rangeMax },
            step: 1,
            format: {
                to: value => Math.round(value),
                from: value => parseFloat(value)
            }
        });
        sliderInstanceRef.current = sliderRef.current.noUiSlider;
        if (isSliderDisabledValue) {
            sliderInstanceRef.current.disable();
        } else if (sliderInstanceRef.current.enable) {
            sliderInstanceRef.current.enable();
        }
        sliderInstanceRef.current.on('update', handleSliderUpdate);
    }, [handleSliderUpdate]);

    // === 副作用処理 ===
    // クエリパラメータの変化を監視して遷移中フラグをリセット
    useEffect(() => {
        setIsNavigating(false);
    }, [location.search]);

    // モーダル開閉時にローカル状態を最新化
    useEffect(() => {
        if (isOpen) {
            // モーダルが初めて開かれる時のみ初期化
            if (!hasModalBeenOpened) {
                const currentType = getFilterValue(salaryTypeParamKey) || SalaryConstants.ALL_TYPE_ID;
                const currentMin = getFilterValue(salaryMinParamKey);
                const currentMax = getFilterValue(salaryMaxParamKey);
                
                setLocalState({
                    salaryType: currentType,
                    salaryMin: currentMin || '',
                    salaryMax: currentMax || ''
                });
                
                // 初期値を種別ごとの保存状態に設定
                setSalaryTypeValues(prev => ({
                    ...prev,
                    [currentType]: {
                        salaryMin: currentMin || '',
                        salaryMax: currentMax || ''
                    }
                }));
                
                setHasModalBeenOpened(true);
            }
        }
    }, [isOpen, getFilterValue, salaryTypeParamKey, salaryMinParamKey, salaryMaxParamKey, hasModalBeenOpened]);

    // フォーマット値の初期化
    useEffect(() => {
        if (isOpen) {
            setFormattedValues({
                min: numberFormat(+initialMin, 0),
                max: numberFormat(+initialMax, 0)
            });
        }
    }, [isOpen, initialMin, initialMax]);

    // スライダーの初期化
    useEffect(() => {
        if (!isOpen) return;
        initializeSlider(initialMin, initialMax, rangeMin, rangeMax, isSliderDisabledValue);
        return () => {
            if (sliderInstanceRef.current) {
                sliderInstanceRef.current.destroy();
                sliderInstanceRef.current = null;
            }
        };
    }, [isOpen, initialMin, initialMax, rangeMin, rangeMax, isSliderDisabledValue, initializeSlider]);

    // === 状態更新関数 ===
    /**
     * ローカル状態を更新
     * @param {Object} updates - 更新内容
     */
    const updateLocalState = useCallback((updates) => {
        setLocalState(prev => ({ ...prev, ...updates }));
    }, []);

    /**
     * フォーマット値を更新
     * @param {Object} updates - 更新内容
     */
    const updateFormattedValues = useCallback((updates) => {
        setFormattedValues(prev => ({ ...prev, ...updates }));
    }, []);

    /**
     * モーダルの開閉状態を更新
     * @param {boolean} isOpen - 開閉状態
     */
    const updateModalState = useCallback((isOpen) => {
        setIsOpen(isOpen);
    }, []);

    return {
        // 状態
        localState,
        isOpen,
        formattedValues,
        isNavigating,
        isSliderDisabledValue,
        rangeMin,
        rangeMax,

        // 参照
        sliderRef,
        sliderInstanceRef,
        minInputRef,
        maxInputRef,

        // 状態更新関数
        updateLocalState,
        updateModalState,

        // スライダー関連処理
        initializeSlider,
        handleSliderUpdate,

        // 給与種別変更処理
        handleSalaryTypeChange,
    };
};

export default useSalaryFilterStateManager; 